import React, { useEffect, useRef, useCallback } from 'react';
import { createIntersectionObserver } from './createIntersectionObserver';

type Props = {
  children: React.ReactElement;
  onChange: (entry: IntersectionObserverEntry) => void;
  rootMargin?: string;
  threshold?: number | number[];
};

const defaultProps = {
  rootMargin: '0px',
  threshold: 0,
};

export function IntersectionObserverComponent({
  children,
  onChange,
  rootMargin = defaultProps.rootMargin,
  threshold = defaultProps.threshold,
}: Props) {
  const targetRef = useRef<Element | null>(null);
  const observerRef = useRef<globalThis.IntersectionObserver | null>(null);
  const previousTargetRef = useRef<Element | null>(null);

  const handleIntersection = useCallback(
    ([entry]: globalThis.IntersectionObserverEntry[]) => {
      onChange(entry);
    },
    [onChange],
  );

  useEffect(() => {
    observerRef.current = createIntersectionObserver(handleIntersection, {
      rootMargin,
      threshold,
    });

    if (targetRef.current) {
      observerRef.current.observe(targetRef.current);
    }

    return () => {
      if (targetRef.current && observerRef.current) {
        observerRef.current.unobserve(targetRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [handleIntersection, rootMargin, threshold]);

  const setRefTarget = useCallback((target: Element | null) => {
    const targetChanged = previousTargetRef.current && previousTargetRef.current !== target;

    if (targetChanged && targetRef.current && observerRef.current) {
      observerRef.current.unobserve(targetRef.current);
    }

    targetRef.current = target;

    if (target && observerRef.current) {
      observerRef.current.observe(target);
    }

    previousTargetRef.current = target;
  }, []);

  return React.cloneElement(React.Children.only(children), {
    ref: setRefTarget,
  });
}

IntersectionObserverComponent.displayName = 'IntersectionObserver';

export { IntersectionObserverComponent as IntersectionObserver };
